import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * A modal built on <dialog>: focus trapping, Escape to close and the backdrop come
 * from the browser. On phones it sits at the bottom like an action sheet.
 */
export function Dialog({ title, onClose, children }: { title: string; onClose(): void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current!;
    d.showModal();
    return () => d.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="sheet"
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Tap on the backdrop closes.
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="sheet-body">
        <h2>{title}</h2>
        {children}
      </div>
    </dialog>
  );
}

export function PromptDialog(props: {
  title: string;
  label: string;
  initial?: string;
  confirm: string;
  onSubmit(value: string): void;
  onClose(): void;
}) {
  const [value, setValue] = useState(props.initial ?? "");
  return (
    <Dialog title={props.title} onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) props.onSubmit(value.trim());
        }}
      >
        <label className="field">
          {props.label}
          <input autoFocus value={value} onChange={(e) => setValue(e.target.value)} onFocus={(e) => e.target.select()} />
        </label>
        <div className="sheet-actions">
          <button type="button" className="quiet" onClick={props.onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={!value.trim()}>
            {props.confirm}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function ConfirmDialog(props: {
  title: string;
  message: ReactNode;
  confirm: string;
  danger?: boolean;
  onConfirm(): void;
  onClose(): void;
}) {
  return (
    <Dialog title={props.title} onClose={props.onClose}>
      <div className="sheet-message">{props.message}</div>
      <div className="sheet-actions">
        <button type="button" className="quiet" autoFocus onClick={props.onClose}>
          Cancel
        </button>
        <button type="button" className={props.danger ? "danger" : "primary"} onClick={props.onConfirm}>
          {props.confirm}
        </button>
      </div>
    </Dialog>
  );
}
